import React from 'react';
import { ChevronDown, Edit2, Save, Users, X } from 'lucide-react';

interface RosterPanelHeaderProps {
  isEditing: boolean;
  saving: boolean;
  isLoading: boolean;
  masterRosterLoading: boolean;
  sessionId: string | null;
  user: any;
  isExpanded: boolean;
  error: string | null;
  successMessage: string | null;
  showSelectionModal: boolean;
  onEditToggle: () => void;
  onSave: () => void;
  onCancel: () => void;
  onToggleExpanded?: () => void;
  disableCollapse?: boolean; // Disable collapse functionality (e.g., in dashboard grid)
  isReadOnlyDemo?: boolean;
}

const RosterPanelHeader: React.FC<RosterPanelHeaderProps> = ({
  isEditing,
  saving,
  isLoading,
  masterRosterLoading,
  sessionId,
  user,
  isExpanded,
  error,
  successMessage,
  showSelectionModal,
  onEditToggle,
  onSave,
  onCancel,
  onToggleExpanded,
  disableCollapse = false,
  isReadOnlyDemo = false,
}) => {
  return (
    <div className="px-3 sm:px-6 py-3 sm:py-5 border-b border-gray-200">
      <div className="flex items-start sm:items-center justify-between gap-2">
        <div className="min-w-0 flex-1">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Users className="h-5 w-5 text-blue-600" />
            Session Roster
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            Review session seat mapping and edit in the mapping modal.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {isEditing ? (
            <>
              <button
                onClick={onSave}
                disabled={saving || masterRosterLoading}
                className="inline-flex items-center gap-1 rounded-md bg-blue-600 px-2.5 sm:px-3 py-1.5 text-xs sm:text-sm font-medium text-white shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-300 whitespace-nowrap"
              >
                <Save className="h-4 w-4" />
                {saving ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={onCancel}
                disabled={saving}
                className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2.5 sm:px-3 py-1.5 text-xs sm:text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60 whitespace-nowrap"
              >
                <X className="h-4 w-4" />
                Cancel
              </button>
            </>
          ) : (
            <button
              onClick={onEditToggle}
              disabled={isLoading || masterRosterLoading || !sessionId || !user || isReadOnlyDemo}
              className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2.5 sm:px-3 py-1.5 text-xs sm:text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60 whitespace-nowrap"
              title={isReadOnlyDemo ? 'Demo sessions are read-only.' : undefined}
            >
              <Edit2 className="h-4 w-4" />
              <span className="hidden sm:inline">Edit Session Roster</span>
              <span className="sm:hidden">Edit Roster</span>
            </button>
          )}
          {!disableCollapse && onToggleExpanded && (
            <button
              onClick={onToggleExpanded}
              className="flex items-center text-gray-500 hover:text-gray-700 transition-colors"
              aria-label={isExpanded ? 'Collapse session roster' : 'Expand session roster'}
            >
              <ChevronDown
                className={`h-5 w-5 transition-transform duration-200 ${
                  isExpanded ? 'rotate-180' : ''
                }`}
              />
            </button>
          )}
        </div>
      </div>
      {(error || successMessage) && !showSelectionModal && (
        <div className="mt-3">
          {error && <p className="text-sm text-red-600">{error}</p>}
          {successMessage && <p className="text-sm text-green-600">{successMessage}</p>}
        </div>
      )}
    </div>
  );
};

export default RosterPanelHeader;
